import { useState } from 'react';
import { Badge, Button, Empty, EmptyDescription, Skeleton } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { readProblem } from '../lib/problem';
import { useFormatHistory, useRestoreFormat } from '../model/useFormatLibrary';
import { BreakingChangeDialog } from './BreakingChangeDialog';

interface HistoryTabProps {
  formatKey: string;
}

/** Every recorded change to the format, newest first, each one a version it can be put back to. */
export function HistoryTab({ formatKey }: HistoryTabProps) {
  const { t, i18n } = useTranslation('admin');
  const history = useFormatHistory(formatKey);
  const restore = useRestoreFormat();
  // A restore that changes the canvas waits here for the same confirmation as an edit would.
  const [awaitingConfirmation, setAwaitingConfirmation] = useState<number | null>(null);

  const restoreTo = async (version: number, confirmBreaking: boolean) => {
    try {
      const result = await restore.mutateAsync({ version, formatKey, confirmBreaking });
      notificationService.showSuccess(
        t('admin.adFormats.history.restored', { version: result.datasetVersion }),
      );
    } catch (error) {
      if (readProblem(error).code === 'breaking_change_unconfirmed') {
        setAwaitingConfirmation(version);
        return;
      }
      notificationService.showError(t('admin.adFormats.errors.generic'));
    }
  };

  const dialog = (
    <BreakingChangeDialog
      open={awaitingConfirmation !== null}
      onCancel={() => setAwaitingConfirmation(null)}
      onConfirm={() => {
        const version = awaitingConfirmation;
        setAwaitingConfirmation(null);
        if (version !== null) void restoreTo(version, true);
      }}
    />
  );

  if (history.isPending) return <Skeleton className="h-24 w-full" />;
  if (!history.data || history.data.length === 0) {
    return (
      <Empty>
        <EmptyDescription>{t('admin.adFormats.history.empty')}</EmptyDescription>
      </Empty>
    );
  }

  const operationLabel = (event: { before: unknown; after: unknown }) =>
    event.before === null
      ? t('admin.adFormats.history.operation.create')
      : event.after === null
        ? t('admin.adFormats.history.operation.delete')
        : t('admin.adFormats.history.operation.update');

  return (
    <>
      {dialog}
      <ul className="flex flex-col divide-y divide-border">
        {history.data.map((event) => (
          <li key={event.id} className="flex flex-wrap items-center gap-sm py-sm">
            <span className="type-label-md">
              {t('admin.adFormats.history.version', { version: event.datasetVersion })}
            </span>
            <Badge variant="secondary">{operationLabel(event)}</Badge>
            <span className="text-body-sm text-muted-foreground">
              {new Date(event.createdAt).toLocaleString(i18n.language)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={restore.isPending}
              onClick={() => void restoreTo(event.datasetVersion, false)}
            >
              {t('admin.adFormats.history.restore')}
            </Button>
          </li>
        ))}
      </ul>
    </>
  );
}
